import { useMutation, useQuery } from '@tanstack/react-query'
import {
  completeUpload,
  createContributorSession,
  createUploadIntent,
  fetchContributorDrop,
  type ContributorDropResponse,
} from '@/lib/contributorWorkerClient'
import { uploadFileToR2 } from '@/lib/uploadClient'

type ContributorQueryRow = NonNullable<ContributorDropResponse['contributor']> & {
  sessionToken: string
}

function normalizeContributor(
  sessionToken: string,
  response: ContributorDropResponse,
): ContributorQueryRow | null {
  if (response.contributor) {
    return { ...response.contributor, sessionToken }
  }
  if (!response.drop || !response.contributorId) return null
  return {
    id: response.contributorId,
    drop_id: response.dropId ?? response.drop.id,
    revoked_at: null,
    drop: response.drop,
    sessionToken,
  }
}

export function useDropByTokenQuery(
  token: string | undefined,
  options: { includePhotos?: boolean; refetchInterval?: number | false } = {},
) {
  const { includePhotos = false, refetchInterval = false } = options
  const query = useQuery({
    queryKey: ['contributor-drop', token, includePhotos],
    queryFn: async () => {
      if (!token) return null
      const session = await createContributorSession(token)
      const drop = await fetchContributorDrop(session.sessionToken, { includePhotos })
      return normalizeContributor(session.sessionToken, drop)
    },
    enabled: !!token,
    refetchInterval,
  })

  return { ...query, contributor: query.data ?? null }
}

export function useInsertPhotoMutation() {
  return useMutation({
    mutationFn: async (params: {
      file?: File
      sessionToken?: string
      contentType?: string
      caption?: string
      fileHash?: string
      object?: {
        id?: string
        drop_id: string
        contributor_id: string
        original_url: string
        thumbnail_url?: string
        caption?: string
        file_size_bytes: number
        file_hash: string
        uploaded_at: string
      }
      contributorToken?: string
    }) => {
      if (!params.file || !params.sessionToken) {
        throw new Error('Photo uploads must use contributor upload-intent and upload-complete worker endpoints.')
      }

      const contentType = params.contentType || params.file.type || 'application/octet-stream'
      const intent = await createUploadIntent({
        sessionToken: params.sessionToken,
        filename: params.file.name,
        contentType,
        sizeBytes: params.file.size,
        caption: params.caption,
        fileHash: params.fileHash,
      })
      await uploadFileToR2(intent.uploadUrl, params.file, contentType)
      await completeUpload({
        key: intent.key,
        uploadCompletionToken: intent.uploadCompletionToken,
        caption: params.caption,
      })
      return intent
    },
  })
}
